import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { Provider, ProviderType, TrackingEvent } from '../types';

interface TwitterConfig {
  pixelIds: string[];
  isEnabled?: boolean;
}

// Define Twitter tracking interface
interface TwqFunction {
  (...args: any[]): void;
  exe?: (...args: any[]) => void;
  queue: any[];
  version: string;
}

// Window interface已在index.d.ts中统一定义

export class TwitterProvider implements Provider {
  type = ProviderType.TWITTER;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: TwitterConfig;

  constructor(config: TwitterConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // Initialize Twitter tracking queue first
      this.initializeTwitterQueue();

      // Load Twitter UWT script
      await this.loadTwitterScript();

      // Configure Twitter pixels
      this.configureTwitterPixels();

      this.initialized = true;
      return true;
    } catch (error) {
      logger.error('Failed to initialize Twitter:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      if (this.loggingEnabled) {
        logger.log('Twitter tracking event:', event);
      }

      if (window.twq) {
        // Track event with Twitter
        if (event.eventName && event.properties) {
          window.twq(event.commandType || 'event', event.eventName, event.properties);
        } else if (event.eventName) {
          window.twq(event.commandType || 'event', event.eventName);
        }
      }
    } catch (error) {
      logger.error('Twitter tracking error:', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  private initializeTwitterQueue(): void {
    // Replicate the Twitter queue initialization script
    const e = window;

    if (!e.twq) {
      // @ts-ignore - Twitter queue initialization requires function assignment
      // eslint-disable-next-line no-multi-assign
      const s = (e.twq = function () {
        if (s.exe) {
          // eslint-disable-next-line prefer-spread, prefer-rest-params
          s.exe.apply(s, arguments);
        } else {
          // eslint-disable-next-line prefer-rest-params
          s.queue.push(arguments);
        }
      } as TwqFunction);

      s.version = '1.1';
      s.queue = [];
    }
  }

  private loadTwitterScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Replicate the Twitter UWT script loading
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const e = window;
        const t = document;
        const n = 'script';

        const u = t.createElement(n) as HTMLScriptElement;
        u.async = true;
        u.src = 'https://static.ads-twitter.com/uwt.js';

        u.onload = () => resolve();
        u.onerror = () => reject(new Error('Failed to load Twitter script'));

        const a = t.getElementsByTagName(n)[0];
        a.parentNode.insertBefore(u, a);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 启用Twitter日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用Twitter日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  private configureTwitterPixels(): void {
    // Configure each pixel ID
    this.config.pixelIds.forEach((pixelId) => {
      window.twq('config', pixelId);
    });
  }
}
